import { useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { useGetMultiInfoCard } from '@package/smart-wallet';
import { useForm } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { z } from 'zod';

import { LoaderSkeleton } from './LoaderSkeleton';

/* Schemas */
export const MultiInfoCardFormSchema = z.object({
  cardIds: z.string().min(1, { message: 'Card IDs are required' }),
});

/* Types */
export type MultiInfoCardFormType = z.infer<typeof MultiInfoCardFormSchema>;

/* Component */
export const MultiInfoCard = () => {
  const [cardIds, setCardIds] = useState<number[]>([]);

  /* Search Form */
  const searchForm = useForm<MultiInfoCardFormType>({
    defaultValues: {
      cardIds: '',
    },
  });

  /* Handlers */
  const handleSearch = async (data: MultiInfoCardFormType) => {
    const ids = data.cardIds.split(',').map((id) => Number(id.trim()));
    setCardIds(ids);
  };

  /* Render */
  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <Form {...searchForm}>
        <form
          onSubmit={searchForm.handleSubmit(handleSearch)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='cardIds'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Card IDs</FormLabel>
                  <FormControl>
                    <Input placeholder='1,2,3' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
          <Button type='submit' className='cursor-pointer w-full'>
            Search card Info by IDs
          </Button>
        </form>
      </Form>
      {!!cardIds.length && <GetInfoCard cardIds={cardIds} />}
    </section>
  );
};

/*Internal components */
type GetInfoCardProps = {
  cardIds: number[];
};

const GetInfoCard = ({ cardIds }: GetInfoCardProps) => {
  const { isLoading, cardsData } = useGetMultiInfoCard({ cardIds });

  return (
    <section className='w-full flex flex-col items-center justify-center'>
      <section className='w-full flex flex-col items-center justify-center gap-4'>
        {!!isLoading && <LoaderSkeleton />}
        {!!cardsData?.length && !isLoading && (
          <section className='w-full flex flex-col items-start justify-start gap-2'>
            <ReactJson collapsed src={cardsData || {}} />
          </section>
        )}
      </section>
    </section>
  );
};
